// Shared types for TrackBible application
// Used across frontend, backend, and infrastructure

export interface User {
  userId: string;
  email: string;
  givenName?: string;
  familyName?: string;
  selectedPlan: ReadingPlan;
  currentBook: string;
  currentChapter: number;
  currentReadThrough: number;
  createdAt: string;
  updatedAt: string;
}

export interface ReadingSession {
  sessionId: string;
  userId: string;
  timerDuration: number; // in minutes
  actualTimeSpent: number; // in minutes
  bookRead: string;
  chaptersRead: number[];
  completedFullDuration: boolean;
  readThrough: number;
  createdAt: string;
}

export interface ChapterProgress {
  userId: string;
  book: string;
  chapter: number;
  readThrough: number;
  completedAt: string;
  sessionId: string;
}

export interface Streak {
  userId: string;
  currentStreak: number;
  longestStreak: number;
  lastReadDate: string | null;
  createdAt: string;
  updatedAt: string;
}

// DynamoDB entity types
export interface DynamoDBEntity {
  PK: string;
  SK: string;
  entityType: EntityType;
}

export type EntityType = 'user' | 'session' | 'progress' | 'streak';

export type UserEntity = User & DynamoDBEntity;
export type SessionEntity = ReadingSession & DynamoDBEntity;
export type ProgressEntity = ChapterProgress & DynamoDBEntity;
export type StreakEntity = Streak & DynamoDBEntity;

// Reading plans
export type ReadingPlan = 'chronological' | 'canonical' | 'oneYear' | 'custom';

export interface BibleBook {
  name: string;
  testament: 'old' | 'new';
  chapters: number;
  abbreviation: string;
}

// API Request/Response types
export interface SignupRequest {
  email: string;
  password: string;
  givenName?: string;
  familyName?: string;
}

export interface SignupResponse {
  message: string;
  userId: string;
  email: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  message: string;
  tokens: {
    accessToken?: string;
    idToken?: string;
    refreshToken?: string;
  };
}

export interface CreateSessionRequest {
  timerDuration: number;
  actualTimeSpent: number;
  bookRead: string;
  chaptersRead: number[];
  completedFullDuration: boolean;
}

export interface CreateSessionResponse {
  message: string;
  sessionId: string;
  timestamp: string;
}

export interface GetSessionsResponse {
  sessions: ReadingSession[];
  count: number;
}

export interface ProgressResponse {
  totalChaptersCompleted: number;
  bookProgress: Record<string, number>;
  completedChapters: ChapterProgress[];
}

export interface StreakResponse {
  currentStreak: number;
  longestStreak: number;
  lastReadDate: string | null;
}

export interface UserProfileResponse {
  currentBook: string | null;
  currentChapter: number | null;
  currentReadThrough: number | null;
  weeklyGoal: number | null;
  email: string | null;
}

export interface UpdatePlanRequest {
  selectedPlan: ReadingPlan;
  currentBook?: string;
  currentChapter?: number;
  currentReadThrough?: number;
}

export interface UpdatePlanResponse {
  message: string;
  selectedPlan: ReadingPlan;
  currentBook?: string;
  currentChapter?: number;
}

// Error response
export interface ErrorResponse {
  message: string;
  error?: string;
}

// Bible metadata
export const BIBLE_BOOKS: BibleBook[] = [
  // Old Testament
  { name: 'Genesis', testament: 'old', chapters: 50, abbreviation: 'Gen' },
  { name: 'Exodus', testament: 'old', chapters: 40, abbreviation: 'Exod' },
  { name: 'Leviticus', testament: 'old', chapters: 27, abbreviation: 'Lev' },
  { name: 'Numbers', testament: 'old', chapters: 36, abbreviation: 'Num' },
  { name: 'Deuteronomy', testament: 'old', chapters: 34, abbreviation: 'Deut' },
  { name: 'Joshua', testament: 'old', chapters: 24, abbreviation: 'Josh' },
  { name: 'Judges', testament: 'old', chapters: 21, abbreviation: 'Judg' },
  { name: 'Ruth', testament: 'old', chapters: 4, abbreviation: 'Ruth' },
  { name: '1 Samuel', testament: 'old', chapters: 31, abbreviation: '1Sam' },
  { name: '2 Samuel', testament: 'old', chapters: 24, abbreviation: '2Sam' },
  { name: '1 Kings', testament: 'old', chapters: 22, abbreviation: '1Kgs' },
  { name: '2 Kings', testament: 'old', chapters: 25, abbreviation: '2Kgs' },
  { name: '1 Chronicles', testament: 'old', chapters: 29, abbreviation: '1Chr' },
  { name: '2 Chronicles', testament: 'old', chapters: 36, abbreviation: '2Chr' },
  { name: 'Ezra', testament: 'old', chapters: 10, abbreviation: 'Ezra' },
  { name: 'Nehemiah', testament: 'old', chapters: 13, abbreviation: 'Neh' },
  { name: 'Esther', testament: 'old', chapters: 10, abbreviation: 'Esth' },
  { name: 'Job', testament: 'old', chapters: 42, abbreviation: 'Job' },
  { name: 'Psalms', testament: 'old', chapters: 150, abbreviation: 'Ps' },
  { name: 'Proverbs', testament: 'old', chapters: 31, abbreviation: 'Prov' },
  { name: 'Ecclesiastes', testament: 'old', chapters: 12, abbreviation: 'Eccl' },
  { name: 'Song of Solomon', testament: 'old', chapters: 8, abbreviation: 'Song' },
  { name: 'Isaiah', testament: 'old', chapters: 66, abbreviation: 'Isa' },
  { name: 'Jeremiah', testament: 'old', chapters: 52, abbreviation: 'Jer' },
  { name: 'Lamentations', testament: 'old', chapters: 5, abbreviation: 'Lam' },
  { name: 'Ezekiel', testament: 'old', chapters: 48, abbreviation: 'Ezek' },
  { name: 'Daniel', testament: 'old', chapters: 12, abbreviation: 'Dan' },
  { name: 'Hosea', testament: 'old', chapters: 14, abbreviation: 'Hos' },
  { name: 'Joel', testament: 'old', chapters: 3, abbreviation: 'Joel' },
  { name: 'Amos', testament: 'old', chapters: 9, abbreviation: 'Amos' },
  { name: 'Obadiah', testament: 'old', chapters: 1, abbreviation: 'Obad' },
  { name: 'Jonah', testament: 'old', chapters: 4, abbreviation: 'Jonah' },
  { name: 'Micah', testament: 'old', chapters: 7, abbreviation: 'Mic' },
  { name: 'Nahum', testament: 'old', chapters: 3, abbreviation: 'Nah' },
  { name: 'Habakkuk', testament: 'old', chapters: 3, abbreviation: 'Hab' },
  { name: 'Zephaniah', testament: 'old', chapters: 3, abbreviation: 'Zeph' },
  { name: 'Haggai', testament: 'old', chapters: 2, abbreviation: 'Hag' },
  { name: 'Zechariah', testament: 'old', chapters: 14, abbreviation: 'Zech' },
  { name: 'Malachi', testament: 'old', chapters: 4, abbreviation: 'Mal' },
  // New Testament
  { name: 'Matthew', testament: 'new', chapters: 28, abbreviation: 'Matt' },
  { name: 'Mark', testament: 'new', chapters: 16, abbreviation: 'Mark' },
  { name: 'Luke', testament: 'new', chapters: 24, abbreviation: 'Luke' },
  { name: 'John', testament: 'new', chapters: 21, abbreviation: 'John' },
  { name: 'Acts', testament: 'new', chapters: 28, abbreviation: 'Acts' },
  { name: 'Romans', testament: 'new', chapters: 16, abbreviation: 'Rom' },
  { name: '1 Corinthians', testament: 'new', chapters: 16, abbreviation: '1Cor' },
  { name: '2 Corinthians', testament: 'new', chapters: 13, abbreviation: '2Cor' },
  { name: 'Galatians', testament: 'new', chapters: 6, abbreviation: 'Gal' },
  { name: 'Ephesians', testament: 'new', chapters: 6, abbreviation: 'Eph' },
  { name: 'Philippians', testament: 'new', chapters: 4, abbreviation: 'Phil' },
  { name: 'Colossians', testament: 'new', chapters: 4, abbreviation: 'Col' },
  { name: '1 Thessalonians', testament: 'new', chapters: 5, abbreviation: '1Thess' },
  { name: '2 Thessalonians', testament: 'new', chapters: 3, abbreviation: '2Thess' },
  { name: '1 Timothy', testament: 'new', chapters: 6, abbreviation: '1Tim' },
  { name: '2 Timothy', testament: 'new', chapters: 4, abbreviation: '2Tim' },
  { name: 'Titus', testament: 'new', chapters: 3, abbreviation: 'Titus' },
  { name: 'Philemon', testament: 'new', chapters: 1, abbreviation: 'Phlm' },
  { name: 'Hebrews', testament: 'new', chapters: 13, abbreviation: 'Heb' },
  { name: 'James', testament: 'new', chapters: 5, abbreviation: 'Jas' },
  { name: '1 Peter', testament: 'new', chapters: 5, abbreviation: '1Pet' },
  { name: '2 Peter', testament: 'new', chapters: 3, abbreviation: '2Pet' },
  { name: '1 John', testament: 'new', chapters: 5, abbreviation: '1John' },
  { name: '2 John', testament: 'new', chapters: 1, abbreviation: '2John' },
  { name: '3 John', testament: 'new', chapters: 1, abbreviation: '3John' },
  { name: 'Jude', testament: 'new', chapters: 1, abbreviation: 'Jude' },
  { name: 'Revelation', testament: 'new', chapters: 22, abbreviation: 'Rev' },
];

export const TOTAL_BIBLE_CHAPTERS = 1189;
export const OLD_TESTAMENT_CHAPTERS = 929;
export const NEW_TESTAMENT_CHAPTERS = 260;
